import _ from 'lodash';
import moment from 'moment';
import { ANNOTATION_TYPE } from '@/shared/ui';
import { getDurationBySecond, getEventDuration } from '@/shared/lib/aiUtils';

/**
 * Normalizes raw holter events into the strip records the ECG events table and viewer render. Copies timing, HR and review fields, derives duration from the event's own value or from start and stop, floors the start to the minute, flattens each strip's annotations into draw entries while synthesizing a sample-mark annotation when only images exist, and mirrors the whole record under originalData.
 *
 * @param holterEvents - Raw holter events returned by the API.
 * @returns An array of formatted event strip records, each carrying its own originalData snapshot.
 */
const formatEventStrip = (holterEvents: any[]) =>
  _.map(holterEvents, (holterEvent: any) => {
    const data = {
      idEvent: holterEvent.id,
      id: new Date(holterEvent.start).getTime(),
      start: holterEvent.start,
      stop: holterEvent.stop,
      duration: holterEvent.duration
        ? getDurationBySecond(holterEvent.duration)
        : getEventDuration(holterEvent.start, holterEvent.stop),
      type: holterEvent.type,
      oldType: holterEvent.type,
      maxHr: holterEvent.maxHr,
      minHr: holterEvent.minHr,
      avgHr: holterEvent.avgHr,
      startTimeEpoch: moment(holterEvent.start).startOf('minutes').valueOf(),
      draw: _.flatten(
        _.map(holterEvent.strips, (strip: any) => {
          const draw =
            strip.draw?.length > 0
              ? strip.draw
              : strip.images?.length > 0
                ? {
                    channelIndex: strip.channel - 1,
                    startTime: strip.start,
                    endTime: strip.stop,
                    type: ANNOTATION_TYPE.SAMPLE_MARK,
                  }
                : [];
          return draw;
        }),
      ),
      stripImgs: holterEvent.stripImgs,
      isReviewed: holterEvent.isReviewed,
      oldReviewed: holterEvent.isReviewed,
      channel: holterEvent.channel || 2,
      technicianComments: holterEvent.stripComment ?? holterEvent.comment,
      isCaptured: holterEvent.isCaptured,
      countBeats: holterEvent.countBeats ?? 0,
      title: holterEvent.title,
      originalEventId: holterEvent.id,
      captures: _.map(holterEvent.strips, (strip: any) => ({
        ...strip,
        stripImgs: strip.images,
        draw:
          strip.draw?.length > 0
            ? strip.draw
            : strip.images?.length > 0
              ? [
                  {
                    channelIndex: strip.channel - 1,
                    startTime: strip.start,
                    endTime: strip.stop,
                    type: ANNOTATION_TYPE.SAMPLE_MARK,
                  },
                ]
              : [],
        technicianComments: strip.comment,
        captureId: strip.id,
        originalId: strip.id,
        originalStrip: strip,
      })),
    };
    return { ...data, originalData: data };
  });

export default formatEventStrip;
